import { useEffect, useState } from "react";
import {
  listTenants, patchTenant,
  snapshotNow, listRetentionSnapshots,
  listRetentionApprovers, addRetentionApprover, removeRetentionApprover,
  requestRetentionRestore, approveRetentionRestore, retentionRestoreStatus, executeRetentionRestore,
  type TenantRow, type RetentionSnapshotView, type RetentionApproverRow, type RetentionRestoreView, type RetentionRestoreEventView,
} from "../api";

export function Retention() {
  const [tenants, setTenants] = useState<TenantRow[] | null>(null);
  const [sel, setSel] = useState<string>("");
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { void loadTenants(); }, []);
  async function loadTenants() {
    try {
      const r = await listTenants();
      setTenants(r.tenants);
      if (!sel && r.tenants.length) setSel(r.tenants[0].tenant_id);
    } catch (e) { setErr(e instanceof Error ? e.message : "load failed"); }
  }

  async function runOp(label: string, op: () => Promise<unknown>) {
    setBusy(label); setMsg(null); setErr(null);
    try { await op(); setMsg(`${label}: ok`); await loadTenants(); }
    catch (e) { setErr(e instanceof Error ? e.message : `${label} failed`); }
    finally { setBusy(null); }
  }

  if (!tenants) return <div>Loading…</div>;
  const tenant = tenants.find(t => t.tenant_id === sel);

  return (
    <>
      <div className="row spaced">
        <h2>Retention</h2>
        <select className="input" value={sel} onChange={e => setSel(e.target.value)}>
          {tenants.map(t => <option key={t.tenant_id} value={t.tenant_id}>{t.display_name}</option>)}
        </select>
      </div>
      {msg && <div className="ok">{msg}</div>}
      {err && <div className="err">{err}</div>}
      {!tenant ? <div className="muted">No tenant selected.</div> : (
        <>
          <PolicyPanel tenant={tenant} busy={busy} runOp={runOp} />
          <ApproverPanel tenantId={tenant.tenant_id} />
          <SnapshotsPanel tenant={tenant} busy={busy} runOp={runOp} />
          <RestorePanel tenantId={tenant.tenant_id} />
        </>
      )}
    </>
  );
}

function PolicyPanel({ tenant, busy, runOp }: { tenant: TenantRow; busy: string | null; runOp: (l: string, op: () => Promise<unknown>) => void }) {
  const t = tenant;
  return (
    <section className="card" style={{ marginTop: 16 }}>
      <h3>Policy</h3>
      <div className="row" style={{ gap: 16, flexWrap: "wrap", alignItems: "center" }}>
        <label className="row" style={{ gap: 6 }}>
          Enabled
          <button className={"badge " + (t.retention_enabled === 1 ? "green" : "grey")} style={{ border: "none", cursor: "pointer" }}
            disabled={busy === `ret:${t.tenant_id}`}
            onClick={() => runOp(`ret:${t.tenant_id}`, () => patchTenant(t.tenant_id, { retention_enabled: t.retention_enabled === 1 ? 0 : 1 }))}>
            {t.retention_enabled === 1 ? "On" : "Off"}
          </button>
        </label>
        <NumField label="keep days" value={t.retention_keep_days ?? 0}
          onCommit={v => runOp(`retkeep:${t.tenant_id}`, () => patchTenant(t.tenant_id, { retention_keep_days: v || null }))} />
        <NumField label="monthly ceiling (AUD)" value={t.retention_monthly_ceiling_aud ?? 0}
          onCommit={v => runOp(`retceil:${t.tenant_id}`, () => patchTenant(t.tenant_id, { retention_monthly_ceiling_aud: v }))} />
        <label className="row muted" style={{ gap: 4, fontSize: 13 }}>
          <input type="checkbox" checked={(t.retention_two_person ?? 0) === 1}
            onChange={e => runOp(`ret2p:${t.tenant_id}`, () => patchTenant(t.tenant_id, { retention_two_person: e.target.checked ? 1 : 0 }))} />
          two-person approval
        </label>
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
        Restore is blocked until a monthly ceiling &gt; 0 and at least one approver group are configured.
      </p>
    </section>
  );
}

function SnapshotsPanel({ tenant, busy, runOp }: { tenant: TenantRow; busy: string | null; runOp: (l: string, op: () => Promise<unknown>) => void }) {
  const [snaps, setSnaps] = useState<RetentionSnapshotView[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  async function load() {
    try { setSnaps((await listRetentionSnapshots(tenant.tenant_id)).snapshots); }
    catch (e) { setLoadErr(e instanceof Error ? e.message : "load failed"); }
  }
  useEffect(() => { void load(); }, [tenant.tenant_id]);
  return (
    <section className="card" style={{ marginTop: 16 }}>
      <div className="row spaced">
        <h3>Snapshots</h3>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn ghost" onClick={() => load()}>Refresh</button>
          <button className="btn" disabled={busy === `snap:${tenant.tenant_id}` || tenant.retention_enabled !== 1}
            onClick={() => runOp(`snap:${tenant.tenant_id}`, async () => { await snapshotNow(tenant.tenant_id); await load(); })}>
            Snapshot now
          </button>
        </div>
      </div>
      {loadErr && <div className="err">{loadErr}</div>}
      <table>
        <thead><tr><th>Captured</th><th>Scope</th><th>Files</th><th>Size</th><th>Packs</th><th>Status</th><th>Expires</th><th>Hold</th><th>ID</th></tr></thead>
        <tbody>
          {(snaps ?? []).map(s => (
            <tr key={s.public_id}>
              <td>{s.captured_at}</td><td>{s.scope}</td><td>{s.file_count}</td>
              <td>{fmtBytes(s.size_bytes)}</td><td>{s.packs}</td>
              <td><span className={"badge " + (s.status === "sealed" ? "green" : s.status === "failed" ? "red" : "amber")}>{s.status}</span></td>
              <td>{s.expires_at ?? "—"}</td><td>{s.legal_hold ? "✓" : ""}</td>
              <td className="mono muted">{s.public_id.slice(0, 8)}</td>
            </tr>
          ))}
          {snaps && !snaps.length && <tr><td colSpan={9} className="muted">No snapshots yet.</td></tr>}
        </tbody>
      </table>
    </section>
  );
}

function ApproverPanel({ tenantId }: { tenantId: string }) {
  const [rows, setRows] = useState<RetentionApproverRow[] | null>(null);
  const [gid, setGid] = useState(""); const [label, setLabel] = useState("");
  const [err, setErr] = useState<string | null>(null);
  async function load() {
    try { setRows((await listRetentionApprovers(tenantId)).approvers); }
    catch (e) { setErr(e instanceof Error ? e.message : "load failed"); }
  }
  useEffect(() => { void load(); }, [tenantId]);
  return (
    <section className="card" style={{ marginTop: 16 }}>
      <h3>Approver groups (Entra)</h3>
      {err && <div className="err">{err}</div>}
      <div className="row" style={{ gap: 6, marginBottom: 8 }}>
        <input className="input" placeholder="Entra group object id" value={gid} onChange={e => setGid(e.target.value)} style={{ width: 280 }} />
        <input className="input" placeholder="label (optional)" value={label} onChange={e => setLabel(e.target.value)} />
        <button className="btn ghost" disabled={!gid.trim()}
          onClick={async () => { try { await addRetentionApprover(tenantId, gid.trim(), label.trim() || undefined); setGid(""); setLabel(""); await load(); } catch (e) { setErr(e instanceof Error ? e.message : "add failed"); } }}>
          Add
        </button>
      </div>
      <table>
        <thead><tr><th>Group id</th><th>Label</th><th></th></tr></thead>
        <tbody>
          {(rows ?? []).map(r => (
            <tr key={r.id}>
              <td className="mono">{r.entraGroupId}</td><td>{r.label ?? ""}</td>
              <td><button className="btn ghost" onClick={async () => { try { await removeRetentionApprover(tenantId, r.id); await load(); } catch (e) { setErr(e instanceof Error ? e.message : "remove failed"); } }}>Remove</button></td>
            </tr>
          ))}
          {rows && !rows.length && <tr><td colSpan={3} className="muted">No approver groups — restore is blocked until at least one is added.</td></tr>}
        </tbody>
      </table>
    </section>
  );
}

function RestorePanel({ tenantId }: { tenantId: string }) {
  const [snapId, setSnapId] = useState("");
  const [token, setToken] = useState("");
  const [okedCost, setOkedCost] = useState(false);
  const [reqId, setReqId] = useState("");
  const [status, setStatus] = useState<RetentionRestoreView | null>(null);
  const [events, setEvents] = useState<RetentionRestoreEventView[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function wrap(fn: () => Promise<void>) { return async () => { setMsg(null); setErr(null); try { await fn(); } catch (e) { setErr(e instanceof Error ? e.message : "failed"); } }; }

  async function refreshStatus(id: string) {
    const s = await retentionRestoreStatus(id);
    setStatus(s.request); setEvents(s.events);
  }

  return (
    <section className="card" style={{ marginTop: 16 }}>
      <h3>Gated restore</h3>
      {msg && <div className="ok">{msg}</div>}
      {err && <div className="err">{err}</div>}
      <div className="row" style={{ gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
        <input className="input" placeholder="snapshot public id" value={snapId} onChange={e => setSnapId(e.target.value)} style={{ width: 300 }} />
        <button className="btn ghost" disabled={!snapId.trim()} onClick={wrap(async () => {
          const r = await requestRetentionRestore(tenantId, snapId.trim(), { wholeScope: true });
          setReqId(r.request.public_id); setMsg(`request ${r.request.public_id.slice(0, 8)} — est ${r.request.est_cost_aud.toFixed(4)} AUD`); await refreshStatus(r.request.public_id);
        })}>Request</button>
      </div>
      {reqId && (
        <>
          <div className="row" style={{ gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input className="input" placeholder="delegated Entra token (approver)" value={token} onChange={e => setToken(e.target.value)} style={{ width: 300 }} />
            <label className="row muted" style={{ gap: 4, fontSize: 13 }}>
              <input type="checkbox" checked={okedCost} onChange={e => setOkedCost(e.target.checked)} /> accept estimated cost
            </label>
            <button className="btn ghost" disabled={!token.trim()} onClick={wrap(async () => {
              const a = await approveRetentionRestore(reqId, tenantId, token.trim(), okedCost);
              setMsg(`approve: ${a.status}${a.reason ? " — " + a.reason : ""}`); await refreshStatus(reqId);
            })}>Approve</button>
            <button className="btn ghost" onClick={wrap(async () => {
              const e = await executeRetentionRestore(reqId, tenantId);
              setMsg(`execute: ${e.status}${e.ready ? " (ready)" : " (thawing — Bulk retrieval up to 48h)"}`); await refreshStatus(reqId);
            })}>Execute / poll</button>
            <button className="btn ghost" onClick={wrap(() => refreshStatus(reqId))}>Refresh status</button>
          </div>
          {status && (
            <div className="mono muted" style={{ fontSize: 12 }}>
              <div>status: <strong>{status.status}</strong> · est {status.est_cost_aud.toFixed(4)} AUD · approvers: {status.approved_by.join(", ") || "—"}</div>
              <div>events: {events.map(ev => ev.event).join(" → ")}</div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function NumField({ label, value, onCommit }: { label: string; value: number; onCommit: (v: number) => void }) {
  const [v, setV] = useState(String(value));
  useEffect(() => { setV(String(value)); }, [value]);
  function commit() { const n = Number(v); if (Number.isFinite(n) && n !== value) onCommit(n); }
  return (
    <span className="row" style={{ gap: 4, alignItems: "center" }} title={label}>
      <span className="muted" style={{ fontSize: 12 }}>{label}</span>
      <input className="input" style={{ width: 80, padding: "2px 4px" }} value={v}
        onChange={e => setV(e.target.value)} onKeyDown={e => { if (e.key === "Enter") commit(); }} onBlur={commit} />
    </span>
  );
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const u = ["KiB", "MiB", "GiB", "TiB"]; let i = -1; let x = n;
  do { x /= 1024; i++; } while (x >= 1024 && i < u.length - 1);
  return `${x.toFixed(1)} ${u[i]}`;
}
